import { useState } from 'react';
import { REGISTRATION_ENABLED } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Trophy, Calendar, Clock, MapPin, KeyRound } from 'lucide-react';
import BracketTreeView from '../BracketTreeView';

interface Tournament {
  id: string;
  name: string;
  date: string;
  endDate?: string;
  club: string;
  modality: 'round-robin' | 'elimination' | 'sum-games';
  gameType: 'singles' | 'doubles';
  sport: 'padel' | 'tennis' | 'pickleball';
  category: 'masculina' | 'femenina' | 'mixtos';
  subcategory: string;
  partner?: string;
  currentStage: string;
  status: 'upcoming' | 'active' | 'finished';
}

interface TournamentEliminationViewProps {
  tournament: Tournament;
  tournamentMatches?: {
    matches: any[];
    standings: any[];
  } | null;
  onRegisterClick?: () => void;
  onAccessCodeClick?: () => void;
  isUserRegistered?: boolean;
  registrationLoading?: boolean;
}

const TournamentEliminationView = ({ tournament, tournamentMatches, onRegisterClick, onAccessCodeClick, isUserRegistered, registrationLoading }: TournamentEliminationViewProps) => {
  const [showFullBracket, setShowFullBracket] = useState(false);

  // Helper function to get readable phase label from DB code
  const getPhaseLabel = (phase?: string): string => {
    const labels: Record<string, string> = {
      'Oct': 'Octavos de Final',
      'R16': 'Octavos de Final',
      'QF': 'Cuartos de Final',
      'SF': 'Semifinales',
      'Final': 'Final'
    };
    return labels[phase || ''] || 'Fase de Eliminación';
  };

  // Generar datos de ejemplo según el tipo de juego
  const generateBracketData = () => {
    if (tournament.gameType === 'singles') {
      return {
        octavos: [
          { id: 1, player1: 'Pérez', player2: 'López', date: '19 Jul', time: '09:00', court: '1', sourceGroup: 'A', result: tournament.status === 'finished' ? 'Pérez 6-4, 6-2' : undefined },
          { id: 2, player1: 'Martín', player2: 'Ruiz', date: '19 Jul', time: '09:30', court: '2', sourceGroup: 'B' },
          { id: 3, player1: 'Castro', player2: 'Herrera', date: '19 Jul', time: '10:00', court: '3', sourceGroup: 'C' },
          { id: 4, player1: 'Morales', player2: 'Vargas', date: '19 Jul', time: '10:30', court: '4', sourceGroup: 'D' },
          { id: 5, player1: 'Jiménez', player2: 'Mendez', date: '19 Jul', time: '11:00', court: '1', sourceGroup: 'E' },
          { id: 6, player1: 'Flores', player2: 'Romero', date: '19 Jul', time: '11:30', court: '2', sourceGroup: 'F' },
          { id: 7, player1: 'Guerrero', player2: 'Sandoval', date: '19 Jul', time: '12:00', court: '3', sourceGroup: 'G' },
          { id: 8, player1: 'Medina', player2: 'Espinoza', date: '19 Jul', time: '12:30', court: '4', sourceGroup: 'H' }
        ],
        cuartos: tournament.status === 'finished' ? [
          { id: 9, player1: 'Pérez', player2: 'Martín', date: '20 Jul', time: '14:00', court: '1', result: 'Pérez 6-3, 6-4' },
          { id: 10, player1: 'Castro', player2: 'Morales', date: '20 Jul', time: '14:30', court: '2', result: 'Castro 7-5, 6-2' },
          { id: 11, player1: 'Jiménez', player2: 'Flores', date: '20 Jul', time: '15:00', court: '3', result: 'Flores 6-4, 3-6, 6-2' },
          { id: 12, player1: 'Guerrero', player2: 'Medina', date: '20 Jul', time: '15:30', court: '4', result: 'Guerrero 6-1, 6-3' }
        ] : [
          { id: 9, player1: 'TBD', player2: 'TBD', date: '20 Jul', time: '14:00', court: '1' },
          { id: 10, player1: 'TBD', player2: 'TBD', date: '20 Jul', time: '14:30', court: '2' },
          { id: 11, player1: 'TBD', player2: 'TBD', date: '20 Jul', time: '15:00', court: '3' },
          { id: 12, player1: 'TBD', player2: 'TBD', date: '20 Jul', time: '15:30', court: '4' }
        ],
        semifinales: tournament.status === 'finished' ? [
          { id: 13, player1: 'Pérez', player2: 'Castro', date: '21 Jul', time: '16:00', court: '1', result: 'Pérez 6-2, 6-4' },
          { id: 14, player1: 'Flores', player2: 'Guerrero', date: '21 Jul', time: '16:30', court: '2', result: 'Guerrero 6-3, 7-5' }
        ] : [
          { id: 13, player1: 'TBD', player2: 'TBD', date: '21 Jul', time: '16:00', court: '1' },
          { id: 14, player1: 'TBD', player2: 'TBD', date: '21 Jul', time: '16:30', court: '2' }
        ],
        final: tournament.status === 'finished' ? [
          { id: 15, player1: 'Pérez', player2: 'Guerrero', date: '22 Jul', time: '17:00', court: '1', result: '🏆 CAMPEÓN: Pérez 6-1, 6-4', winner: 'Pérez' }
        ] : [
          { id: 15, player1: 'TBD', player2: 'TBD', date: '22 Jul', time: '17:00', court: '1' }
        ]
      };
    } else {
      // Datos para doubles (formato pareja)
      return {
        octavos: [
          { id: 1, player1: 'Pérez/Silva', player2: 'López/García', date: '19 Jul', time: '09:00', court: '1', sourceGroup: 'A' },
          { id: 2, player1: 'Martín/Torres', player2: 'Ruiz/Vega', date: '19 Jul', time: '09:30', court: '2', sourceGroup: 'B' },
          { id: 3, player1: 'Castro/Luna', player2: 'Herrera/Ramos', date: '19 Jul', time: '10:00', court: '3', sourceGroup: 'C' },
          { id: 4, player1: 'Morales/Díaz', player2: 'Vargas/Soto', date: '19 Jul', time: '10:30', court: '4', sourceGroup: 'D' },
          { id: 5, player1: 'Jiménez/Cruz', player2: 'Mendez/Ortiz', date: '19 Jul', time: '11:00', court: '1', sourceGroup: 'E' },
          { id: 6, player1: 'Flores/Aguilar', player2: 'Romero/Núñez', date: '19 Jul', time: '11:30', court: '2', sourceGroup: 'F' },
          { id: 7, player1: 'Guerrero/Reyes', player2: 'Sandoval/Campos', date: '19 Jul', time: '12:00', court: '3', sourceGroup: 'G' },
          { id: 8, player1: 'Medina/Cortés', player2: 'Espinoza/Valdez', date: '19 Jul', time: '12:30', court: '4', sourceGroup: 'H' }
        ],
        cuartos: [
          { id: 9, player1: 'TBD', player2: 'TBD', date: '20 Jul', time: '14:00', court: '1' },
          { id: 10, player1: 'TBD', player2: 'TBD', date: '20 Jul', time: '14:30', court: '2' },
          { id: 11, player1: 'TBD', player2: 'TBD', date: '20 Jul', time: '15:00', court: '3' },
          { id: 12, player1: 'TBD', player2: 'TBD', date: '20 Jul', time: '15:30', court: '4' }
        ],
        semifinales: [
          { id: 13, player1: 'TBD', player2: 'TBD', date: '21 Jul', time: '16:00', court: '1' },
          { id: 14, player1: 'TBD', player2: 'TBD', date: '21 Jul', time: '16:30', court: '2' }
        ],
        final: [
          { id: 15, player1: 'TBD', player2: 'TBD', date: '22 Jul', time: '17:00', court: '1' }
        ]
      };
    }
  };

  const bracketData = generateBracketData();

  // Grupo del usuario (ejemplo: grupo A)
  const userGroup = 'A';

  // Use dynamic match data if available, otherwise use generated data
  const getCurrentUserMatch = () => {
    if (tournamentMatches?.matches && tournamentMatches.matches.length > 0) {
      // Find the upcoming match from dynamic data
      return tournamentMatches.matches.find(match => match.isUpcoming);
    }
    // Fallback to generated bracket data
    const userMatch = bracketData.octavos.find(match => match.sourceGroup === userGroup);
    return userMatch;
  };

  const currentMatch = getCurrentUserMatch();

  if (showFullBracket) {
      return (
        <BracketTreeView 
          bracketData={bracketData}
          sport={tournament.sport}
          gameType={tournament.gameType}
          userGroup={userGroup}
          onBack={() => setShowFullBracket(false)}
          showBackButton={true}
          modality={tournament.modality}
        />
      );
  }

  return (
    <div className="space-y-6">
      {/* Registration Buttons - Solo si NO está inscrito */}
      {!isUserRegistered && onRegisterClick && (
        <div className="px-0 space-y-2">
          {/* Access Code Button */}
          {onAccessCodeClick && (
            <Button
              variant="ghost"
              onClick={onAccessCodeClick}
              className="w-full text-slate-600 hover:text-slate-800 hover:bg-slate-100"
            >
              <KeyRound className="h-4 w-4 mr-2" />
              ¿Tienes un código de acceso?
            </Button>
          )}
          {/* Main Registration Button */}
          <Button
            onClick={REGISTRATION_ENABLED ? onRegisterClick : undefined}
            disabled={registrationLoading || !REGISTRATION_ENABLED}
            className={`w-full h-16 text-lg font-bold shadow-lg hover:shadow-xl transition-all ${
              !REGISTRATION_ENABLED
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-primary hover:bg-primary/90 text-primary-foreground'
            }`}
          >
            {registrationLoading ? 'Cargando...' : '¡Inscríbeme!'}
          </Button>
        </div>
      )}

      {/* Tu Próximo Partido */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Trophy className="h-5 w-5 text-yellow-600" />
              <CardTitle className="text-base font-bold text-gray-900">
                Tu Próximo Partido
              </CardTitle>
            </div>
            <Button 
              onClick={() => setShowFullBracket(true)}
              variant="outline"
              className="text-primary border-primary/30 hover:bg-primary/5 px-2 py-1 text-xs h-auto"
            >
              <Trophy className="mr-1 h-2.5 w-2.5" />
              Ver Brackets
            </Button>
          </div>
          <p className="text-gray-600">
            Tu partido programado en la fase de eliminación directa
          </p>
        </CardHeader>
        <CardContent>
          {currentMatch ? (
            <div className="border rounded-lg p-4 bg-primary/5 border-primary/20">
              <div className="flex items-center justify-between mb-3">
              <Badge className="bg-primary text-primary-foreground font-semibold">
                  {tournament.status === 'finished' ? 'Torneo Finalizado' : getPhaseLabel((currentMatch as any)?.phase)}
                </Badge>
                <Badge variant="outline" className={tournament.status === 'finished' ? "border-yellow-500 text-yellow-700" : "border-green-500 text-green-700"}>
                  {tournament.status === 'finished' ? '🏆 Completado' : 'Programado'}
                </Badge>
              </div>
              <div className="space-y-2">
                <div className="text-center">
                  <span className="font-medium text-gray-900">
                    {tournament.gameType === 'singles' ? 'Tú' : `Tú${tournament.partner ? ` / ${tournament.partner}` : ''}`}
                  </span>
                </div>
                <div className="text-center text-gray-500 text-sm font-medium">VS</div>
                <div className="text-center">
                  <span className="font-medium text-gray-900">
                    {currentMatch.opponent || currentMatch.player2}
                  </span>
                </div>
                <div className="flex items-center space-x-4 text-sm text-gray-600 pt-2 border-t border-blue-200">
                  <div className="flex items-center space-x-1">
                    <Calendar className="h-4 w-4" />
                    <span>{currentMatch.date}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Clock className="h-4 w-4" />
                    <span>{currentMatch.time}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <MapPin className="h-4 w-4" />
                    <span>Cancha {currentMatch.court}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <p>{tournament.status === 'finished' ? 'Torneo finalizado - Ver resultados completos en brackets' : 'No tienes partidos programados en este momento'}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TournamentEliminationView;